import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import API_BASE_URL from '../../config/api';
import { useAlert } from '../../context/AlertContext';
import { Receipt, Search, Filter, Calendar, RefreshCw, Eye, FileText, Package, CreditCard } from 'lucide-react';
import InvoiceView from '../../components/InvoiceView';
import AutoRefreshIndicator from '../../components/AutoRefreshIndicator';

const AUTO_REFRESH_INTERVAL = 30000;

const InvoiceManagement = () => {
    const { showAlert } = useAlert();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    const fetchInvoices = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setIsRefreshing(true);

        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/invoices.php`);
            url.searchParams.append('list', '1');
            url.searchParams.append('_t', Date.now());
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            if (typeFilter !== 'all') url.searchParams.append('type', typeFilter);

            const response = await fetch(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'no-cache'
                }
            });
            const data = await response.json();

            if (data.success && isMountedRef.current) {
                setInvoices(data.data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            if (!silent) showAlert('Failed to fetch invoices', 'error');
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [showAlert, fromDate, toDate, typeFilter]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchInvoices(false);
        return () => { isMountedRef.current = false; };
    }, [fetchInvoices]);

    useEffect(() => {
        if (autoRefreshEnabled) {
            intervalRef.current = setInterval(() => fetchInvoices(true), AUTO_REFRESH_INTERVAL);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [autoRefreshEnabled, fetchInvoices]);

    const filteredInvoices = invoices.filter(inv => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (inv.invoice_number || '').toLowerCase().includes(searchLower) ||
            (inv.customer_name || '').toLowerCase().includes(searchLower) ||
            (inv.customer_id || '').toLowerCase().includes(searchLower)
        );
    });

    const totalAmount = filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
    const epinCount = filteredInvoices.filter(i => i.invoice_type === 'epin').length;
    const productCount = filteredInvoices.filter(i => i.invoice_type === 'product').length;

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <Receipt className="text-red-600" size={28} />
                            Invoices
                        </h1>
                        <p className="text-gray-500 text-sm mt-1 font-medium">All generated bills and invoices</p>
                    </div>
                    <AutoRefreshIndicator
                        lastUpdated={lastUpdated}
                        isAutoRefreshEnabled={autoRefreshEnabled}
                        onToggle={() => setAutoRefreshEnabled(prev => !prev)}
                        onRefresh={() => fetchInvoices(false)}
                        loading={isRefreshing}
                        intervalSeconds={30}
                    />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Invoices', value: filteredInvoices.length, icon: FileText, color: 'indigo' },
                        { label: 'E-Pin Bills', value: epinCount, icon: CreditCard, color: 'blue' },
                        { label: 'Product Bills', value: productCount, icon: Package, color: 'green' },
                        { label: 'Total Value', value: `₹${totalAmount.toLocaleString()}`, icon: Receipt, color: 'red' }
                    ].map((stat, idx) => (
                        <div key={idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                                    <p className="text-2xl font-black text-gray-900 mt-1">{stat.value}</p>
                                </div>
                                <div className={`p-3 bg-${stat.color}-50 text-${stat.color}-600 rounded-xl`}>
                                    <stat.icon size={22} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="flex flex-col md:flex-row gap-3 items-center">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by invoice no., customer name, or ID..."
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm font-medium"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer" />
                            </div>
                            <span className="text-gray-300 text-xs font-bold">to</span>
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer" />
                            </div>

                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <select
                                    className="pl-9 pr-8 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none bg-white font-bold text-xs"
                                    value={typeFilter}
                                    onChange={e => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="epin">E-Pin Bills</option>
                                    <option value="product">Product Bills</option>
                                </select>
                            </div>

                            <button
                                onClick={() => fetchInvoices(false)}
                                className="px-4 py-2.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-100 flex items-center gap-2"
                            >
                                <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                                Filter
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                            <p className="text-gray-500 mt-4">Loading invoices...</p>
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="p-12 text-center">
                            <Receipt className="mx-auto text-gray-300" size={48} />
                            <p className="text-gray-400 mt-4 font-bold">No invoices found</p>
                            <p className="text-gray-400 mt-1 text-sm">Invoices are generated automatically when E-Pin requests or product orders are approved</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Invoice #</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredInvoices.map(inv => (
                                        <tr key={inv.id} className="hover:bg-red-50/30 transition-colors cursor-pointer" onClick={() => setSelectedInvoiceId(inv.id)}>
                                            <td className="px-4 py-3">
                                                <span className="font-black text-gray-900">{inv.invoice_number}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${inv.invoice_type === 'epin'
                                                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                                    : 'bg-green-50 text-green-700 border border-green-100'
                                                    }`}>
                                                    {inv.invoice_type === 'epin' ? <CreditCard size={12} /> : <Package size={12} />}
                                                    {inv.invoice_type === 'epin' ? 'E-Pin' : 'Product'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="font-bold text-gray-900 text-sm">{inv.customer_name}</p>
                                                <p className="text-xs text-gray-400 font-medium">{inv.customer_id}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-sm font-bold text-gray-600">
                                                    {new Date(inv.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <span className="font-black text-gray-900">₹{parseFloat(inv.total_amount).toLocaleString()}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedInvoiceId(inv.id); }}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                                    title="View Invoice"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Invoice View Modal */}
            {selectedInvoiceId && (
                <InvoiceView
                    invoiceId={selectedInvoiceId}
                    onClose={() => { setSelectedInvoiceId(null); fetchInvoices(true); }}
                    isAdmin={true}
                />
            )}
        </Layout>
    );
};

export default InvoiceManagement;
